import { Platform, StyleSheet, FlatList, Dimensions, Animated, useWindowDimensions, Pressable } from "react-native";
import React, { useCallback, useEffect, useMemo, useRef, useState as useStateHook } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Text, View } from "@constants/Themed";
import Modal from 'react-native-modal';
import { HIDE_NOTIFICATIONS_PANEL, UPDATE_PAGE_BADGES, NEW_NOTIFICATION } from '@reduxLocal/actions/types';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { useState } from "react";
import SocketManager from "@utils/socket";
import NotificationItem from "./components/NotificationItem";
import { useNavigation } from "@react-navigation/native";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { Sound } from "expo-av/build/Audio/Sound";
import { Tab, Tabs } from "@mui/material";
import RequestItem from "./components/RequestItem";
import InviteItem from "./components/InviteItem";
import ErrorBoundary from "@utils/ErrorBoundary";
import NoticeItem from "./components/NoticeItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from 'i18next';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import apis from '@api/api';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import LottieView from '@utils/lottieAnimation';
import { YStack } from 'tamagui';
import CloseButton from "../../UI/CloseButton";

const socketManager = new SocketManager('notifications');

// Component for displaying grouped notifications
const GroupedNotificationItem = ({ group, theme }: { group: any; theme: boolean }) => {
  const [isExpanded, setIsExpanded] = useStateHook(false);
  const representative = group.representative;

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      style={[
        getModernCardStyle(theme, 'small'),
        {
          marginVertical: 8,
          marginHorizontal: 4,
          overflow: 'hidden',
        }
      ]}
    >
      {/* Main notification item (representative) with badge overlay */}
      <View style={{ position: 'relative' }}>
        <Pressable onPress={() => setIsExpanded(!isExpanded)}>
          <NotificationItem item={representative} />
        </Pressable>

        {/* Badge showing group count */}
        <Animatable.View
          animation="bounceIn"
          delay={100}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: theme ? '#5ac0ea' : '#7dd3fc',
            borderRadius: 12,
            minWidth: 24,
            minHeight: 24,
            paddingHorizontal: 8,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 2,
            borderColor: theme ? '#fff' : '#1e293b',
            zIndex: 10,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
          }}
        >
          <Text
            style={{
              color: theme ? '#fff' : '#1e293b',
              fontSize: 12,
              fontWeight: '700',
            }}
          >
            {group.count}
          </Text>
        </Animatable.View>

        {/* Expand/Collapse indicator */}
        <Pressable
          onPress={() => setIsExpanded(!isExpanded)}
          style={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            zIndex: 10,
            padding: 4,
            backgroundColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: 12,
          }}
        >
          <AntDesign
            name={isExpanded ? 'up' : 'down'}
            size={16}
            color={getModernTextColor(theme, 'muted')}
          />
        </Pressable>
      </View>

      {/* Expanded list of grouped notifications */}
      {isExpanded && (
        <Animatable.View
          animation="slideDown"
          duration={200}
          style={{
            borderTopWidth: 1,
            borderTopColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            paddingTop: 8,
            paddingBottom: 8,
            paddingHorizontal: 16,
            maxHeight: 400,
            overflow: 'hidden',
          }}
        >
          <FlatList
            data={group.notifications.slice(1)} // Skip the first one (already shown as representative)
            scrollEnabled={true}
            nestedScrollEnabled={true}
            style={{ maxHeight: 384 }}
            contentContainerStyle={{ paddingBottom: 8 }}
            keyExtractor={(item) => item._id || `grouped_${item.timestamp}`}
            renderItem={({ item }) => (
              <View
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  marginVertical: 4,
                  backgroundColor: theme ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 8,
                  borderLeftWidth: 3,
                  borderLeftColor: theme ? '#5ac0ea' : '#7dd3fc',
                }}
              >
                <NotificationItem item={item} />
              </View>
            )}
          />
        </Animatable.View>
      )}
    </Animatable.View>
  );
};

const NotificationModal = () => {
  const visibleState = useSelector((state: DefaultRootState) => state.modals.notificationsPanel?.modalDisplayed);
  const notifications = useSelector((state: DefaultRootState) => state.notifications);
  const theme = themeSelector();
  const [event, setEvent] = useState(notifications?.notificationList);
  const authenticated = useSelector((state: DefaultRootState) => state.promiseData?.isAuthenticated || false);
  const userId = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData?.id);
  
  // Track socket ID and connection state to detect reconnections
  // The proxy object uses getters, so React won't detect changes - we need to poll
  const [socketId, setSocketId] = useState<string | undefined>(undefined);
  const [socketConnected, setSocketConnected] = useState(false);
  
  // Get socket instance - ensure we use the same instance
  // Use useMemo to prevent socket object from changing on every render
  const socket = useMemo(() => {
    const sock = socketManager.getSocket(userId);
    console.log('🔔 NotificationModal: Getting socket instance', {
      userId,
      socketId: sock?.id,
      connected: sock?.connected,
      namespace: sock?.nsp?.name
    });
    return sock;
  }, [userId]);
  
  // Poll socket state periodically to detect reconnections (proxy uses getters, so React doesn't detect changes)
  useEffect(() => {
    if (!socket) return;
    
    const checkSocketState = () => {
      const currentSocketId = socket.id;
      const currentConnected = socket.connected;
      
      if (currentSocketId !== socketId || currentConnected !== socketConnected) {
        setSocketId(currentSocketId);
        setSocketConnected(currentConnected);
        console.log('🔔 NotificationModal: Socket state updated', {
          socketId: currentSocketId,
          connected: currentConnected,
          previousSocketId: socketId
        });
      }
    };
    
    // Check immediately
    checkSocketState();
    
    // Poll every second to detect reconnections
    const interval = setInterval(checkSocketState, 1000);
    
    return () => clearInterval(interval);
  }, [socket, socketId, socketConnected]);
  
  const [selectedTab, setSelectedTab] = useState(0);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentUserId = selectUser()?.id;

  // Debug: Log socket instance details (use tracked state for reliable updates)
  useEffect(() => {
    console.log('🔔 NotificationModal: Socket instance:', {
      socketId: socketId,
      connected: socketConnected,
      namespace: socket?.nsp?.name,
      hasSocket: !!socket,
      socketManagerNamespace: socketManager.namespace,
      actualSocketId: socket?.id, // For comparison
      actualConnected: socket?.connected // For comparison
    });
  }, [socketId, socketConnected, socket]);

  // Fetch notification logs from API
  const { data: userNotificationLogs } = useQuery({
    queryKey: ['userNotificationLogs', userId],
    queryFn: async () => {
      if (!userId) return [];
      const response = await apis.getUserNotificationLogs(500);
      return response?.data || [];
    },
    enabled: !!userId && authenticated,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false
  });

  const companyId = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData?.companyId);
  const companyNumber = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData?.companyNumber);
  const effectiveCompanyId = companyNumber || companyId;

  const { data: companyNotificationLogs } = useQuery({
    queryKey: ['companyNotificationLogs', effectiveCompanyId],
    queryFn: async () => {
      if (!effectiveCompanyId) return [];
      const response = await apis.getCompanyNotificationLogs(5000);
      return response?.data || [];
    },
    enabled: !!effectiveCompanyId && authenticated,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false
  });

  // Merge notification logs with real-time notifications on initial load
  useEffect(() => {
    const logsToMerge: any[] = [];

    // Add user notification logs
    if (userNotificationLogs && Array.isArray(userNotificationLogs)) {
      logsToMerge.push(...userNotificationLogs.map((log: any) => ({
        ...log.notificationData,
        _id: log.notificationId || log._id,
        timestamp: log.timestamp || log.notificationData?.timestamp,
        noticeType: 'notification',
        fromLog: true // Flag to identify log entries
      })));
    }

    // Add company notification logs
    if (companyNotificationLogs && Array.isArray(companyNotificationLogs)) {
      logsToMerge.push(...companyNotificationLogs.map((log: any) => ({
        ...log.notificationData,
        _id: log.notificationId || log._id,
        timestamp: log.timestamp || log.notificationData?.timestamp,
        noticeType: 'notification',
        fromLog: true // Flag to identify log entries
      })));
    }

    if (logsToMerge.length > 0) {
      console.log('🔔 NotificationModal: Merging', logsToMerge.length, 'notification logs');
      setEvent((prevEvents) => {
        // Create a set of existing notification IDs
        const existingIds = new Set(prevEvents.map((e: any) => {
          return e._id?.toString() || e.notificationId?.toString() || e.timestamp?.toString();
        }));

        // Filter out logs that already exist in current events
        const newLogs = logsToMerge.filter((log: any) => {
          const id = log._id?.toString() || log.notificationId?.toString() || log.timestamp?.toString();
          return id && !existingIds.has(id);
        });

        if (newLogs.length > 0) {
          // Merge logs with existing events, sort by timestamp (newest first)
          const merged = [...newLogs, ...prevEvents];
          merged.sort((a: any, b: any) => {
            const timeA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : (a.timestamp || 0);
            const timeB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : (b.timestamp || 0);
            return timeB - timeA;
          });
          return merged;
        }

        return prevEvents;
      });
    }
  }, [userNotificationLogs, companyNotificationLogs]);

  // Sync local state with Redux notification list
  // This ensures notifications added via Redux (from statusBar or DashStack) are reflected in the modal
  useEffect(() => {
    if (notifications?.notificationList && Array.isArray(notifications.notificationList)) {
      console.log('🔔 NotificationModal: Syncing with Redux notification list:', notifications.notificationList.length, 'notifications');
      setEvent((prevEvents) => {
        // Merge Redux notifications with local state, avoiding duplicates
        const reduxNotifications = notifications.notificationList;
        const existingIds = new Set(prevEvents.map((e: any) => e._id?.toString() || e.notificationId?.toString() || e.timestamp?.toString()));
        
        // Find new notifications from Redux that aren't in local state
        const newNotifications = reduxNotifications.filter((notif: any) => {
          const id = notif._id?.toString() || notif.notificationId?.toString() || notif.timestamp?.toString();
          return id && !existingIds.has(id);
        });
        
        // If there are new notifications, merge them
        if (newNotifications.length > 0) {
          console.log('🔔 NotificationModal: Found', newNotifications.length, 'new notifications from Redux to merge');
          // Sort by timestamp (newest first) after merging
          const merged = [...newNotifications, ...prevEvents];
          merged.sort((a: any, b: any) => {
            const timeA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : (a.timestamp || 0);
            const timeB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : (b.timestamp || 0);
            return timeB - timeA;
          });
          return merged;
        }
        
        // If Redux list is longer, it means we might have missed some - sync fully
        if (reduxNotifications.length > prevEvents.length) {
          console.log('🔔 NotificationModal: Redux list is longer, performing full sync');
          return [...reduxNotifications];
        }
        
        return prevEvents;
      });
    }
  }, [notifications?.notificationList, notifications?.lastNotification]);

  // Fetch invitations using useQuery (same as InvitationsScreen)
  const { data: invitationsData, isLoading: isLoadingInvitations } = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const res = await apis.getInvitations();
      return res;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
    enabled: !!currentUserId, // Only fetch if user is logged in
  });

  // NOTE: Notification socket listeners are now handled centrally in DashStack.tsx
  // NotificationModal will receive notifications through Redux (NEW_NOTIFICATION action)
  // This prevents duplicate socket connections and ensures single source of truth
  // The modal will still respond to Redux state changes and display notifications accordingly
  
  // Keep this useEffect for any NotificationModal-specific setup that doesn't involve socket listeners
  useEffect(() => {
    console.log('🔔 NotificationModal: useEffect triggered', {
      authenticated,
      userId,
      hasSocket: !!socket,
      socketConnected: socket?.connected,
      socketId: socket?.id
    });

    // NOTE: Socket listeners removed - handled in DashStack.tsx
    // NotificationModal will receive notifications through Redux (NEW_NOTIFICATION action)
    // This prevents duplicate socket connections and ensures single source of truth
    if (authenticated && userId) {
      console.log('🔔 NotificationModal: Initialized (socket listeners handled in DashStack)');
      // No socket setup needed - all notification handling is done in DashStack.tsx
      // Notifications will come through Redux (NEW_NOTIFICATION action)
    } else {
      console.log('🔔 NotificationModal: Skipping socket setup - not authenticated or no userId', {
        authenticated,
        userId
      });
    }
  }, [userId, dispatch, authenticated, socket])

  const handleEventListUpdate = useCallback((newEvents) => {
    //Ensure no duplicates
    setEvent((prevEvents) => {
      const filteredEvents = newEvents.filter((newEvent) => {
        return !prevEvents.find((oldEvent) => oldEvent._id === newEvent._id);
      });
      return [...prevEvents, ...filteredEvents];
    });
  }, []);

  const _onAccept = useCallback((id) => {
    //Remove the event from the list
    setEvent((prevEvents) => prevEvents.filter((event) => event._id !== id));
    // Invalidate invitations query to refresh data
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  }, [queryClient]);

  const _onReject = useCallback((id) => {
    //Remove the event from the list
    setEvent((prevEvents) => prevEvents.filter((event) => event._id !== id));
    // Invalidate invitations query to refresh data
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  }, [queryClient]);

  // Sync invitations from query into event list
  useEffect(() => {
    if (!invitationsData || !Array.isArray(invitationsData) || !currentUserId) {
      // Clear invitations if no data or no user
      setEvent((prevEvents) =>
        prevEvents.filter((event) => event.noticeType !== 'invitation')
      );
      return;
    }

    try {
      // Filter to only show received invitations (where current user is the recipient)
      // Same logic as InvitationsScreen when filter is false
      const receivedInvitations = invitationsData.filter(
        (item: any) => item.to?._id === currentUserId || item.to?._id?.toString() === currentUserId?.toString()
      );

      if (receivedInvitations.length > 0) {
        // Map invitations to include noticeType
        const invitationsWithType = receivedInvitations.map((item: any) => ({
          ...item,
          noticeType: 'invitation',
        }));

        // Update event list: remove old invitations and add new ones
        setEvent((prevEvents) => {
          // Remove old invitations
          const withoutInvitations = prevEvents.filter(
            (event) => event.noticeType !== 'invitation'
          );

          // Add new invitations, ensuring no duplicates
          const existingIds = new Set(withoutInvitations.map((e) => e._id?.toString()));
          const newInvitations = invitationsWithType.filter(
            (inv) => !existingIds.has(inv._id?.toString())
          );

          return [...withoutInvitations, ...newInvitations];
        });
      } else {
        // No invitations, remove any existing invitation items
        setEvent((prevEvents) =>
          prevEvents.filter((event) => event.noticeType !== 'invitation')
        );
      }
    } catch (err) {
      console.warn('Failed to process invitations:', err);
      setEvent((prevEvents) =>
        prevEvents.filter((event) => event.noticeType !== 'invitation')
      );
    }
  }, [invitationsData, currentUserId]);


  //Add listener for redux NEW_NOTIFICATION
  // Use refs to ensure single instance of tracker and badgeUpdater across re-renders
  const utilsRef = useRef<any>(null);
  const deduplicationTrackerRef = useRef<any>(null);
  const badgeUpdaterRef = useRef<any>(null);
  const utilsPromiseRef = useRef<Promise<any> | null>(null);
  // Track recent badge updates to prevent duplicate updates for the same page within a short window
  const recentBadgeUpdatesRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Initialize utilities and tracker - cache the promise
    const initUtils = async () => {
      if (!utilsPromiseRef.current) {
        utilsPromiseRef.current = import('@utils/notifications/notificationUtils').then((importedUtils) => {
          utilsRef.current = importedUtils;
          if (!deduplicationTrackerRef.current) {
            deduplicationTrackerRef.current = new importedUtils.NotificationDeduplicationTracker(100);
          }
          if (!badgeUpdaterRef.current) {
            badgeUpdaterRef.current = new importedUtils.DebouncedBadgeUpdater(
              dispatch,
              UPDATE_PAGE_BADGES,
              300 // 300ms debounce delay
            );
          }
          return importedUtils;
        });
      }
      return utilsPromiseRef.current;
    };

    // Initialize immediately
    initUtils();

    const unsubscribe = store.subscribe(() => {
      const lastAction = store.getState().lastAction;
      // Add null check and verify action type matches
      if (lastAction && lastAction.type === NEW_NOTIFICATION) {
        const newEvent = lastAction.payload;

        // Process notification when utils are loaded
        const processNotification = async (notificationUtils: any, tracker: any, updater: any) => {
          // Use shared utility for deduplication
          if (tracker && tracker.hasBeenProcessed(newEvent)) {
            console.log('🔔 NotificationModal: Already processed this notification, skipping badge update');
            return;
          }
          if (tracker) {
            tracker.markAsProcessed(newEvent);
          }

          try {
            // Handle MongoDB change stream events
            const page = notificationUtils.getPageBadgeForNotification(newEvent);
            if (page && updater) {
              // Prevent duplicate badge updates for the same page within 1 second
              // This helps when multiple change stream events fire for a single save operation
              const now = Date.now();
              const lastUpdateTime = recentBadgeUpdatesRef.current.get(page) || 0;
              const timeSinceLastUpdate = now - lastUpdateTime;
              
              // Only update badge if it's been more than 1 second since last update for this page
              // This prevents multiple badge updates when many notifications arrive rapidly
              if (timeSinceLastUpdate > 1000) {
                // Use debounced badge updater instead of direct dispatch
                // This will batch multiple calls within the debounce delay
                updater.updateBadge(page, 1);
                recentBadgeUpdatesRef.current.set(page, now);
              } else {
                console.log('🔔 NotificationModal: Skipping badge update - too soon after previous update for page:', page, 'timeSinceLastUpdate:', timeSinceLastUpdate, 'ms');
              }
            }
            // Handle job/test notifications (they may not have ns.coll)
            else if (notificationUtils.isJobOrTestNotification(newEvent)) {
              console.log('🔔 NotificationModal: Processing job/test notification');
              // Job/test notifications don't need page badges, but we still want to show them
            }
          } catch (e) {
            console.log('🔔 NotificationModal: Error processing notification:', e);
          }

          // Normalize notification using shared utility
          const normalizedEvent = notificationUtils.normalizeNotification(newEvent);

          console.log('🔔 NotificationModal: Adding event to list:', normalizedEvent);
          setEvent((prevEvents) => {
            // Check for duplicates using shared utility
            const isDuplicate = prevEvents.some(evt =>
              notificationUtils.areNotificationsDuplicate(evt, normalizedEvent)
            );
            if (isDuplicate) {
              console.log('🔔 NotificationModal: Duplicate notification in list, skipping');
              return prevEvents;
            }
            return [normalizedEvent, ...prevEvents];
          });
        };

        // If utils are already loaded, process immediately; otherwise wait for them
        if (utilsRef.current && deduplicationTrackerRef.current && badgeUpdaterRef.current) {
          processNotification(utilsRef.current, deduplicationTrackerRef.current, badgeUpdaterRef.current);
        } else {
          // Ensure utils are loaded before processing
          initUtils().then((importedUtils) => {
            if (!deduplicationTrackerRef.current) {
              deduplicationTrackerRef.current = new importedUtils.NotificationDeduplicationTracker(100);
            }
            if (!badgeUpdaterRef.current) {
              badgeUpdaterRef.current = new importedUtils.DebouncedBadgeUpdater(
                dispatch,
                UPDATE_PAGE_BADGES,
                300
              );
            }
            processNotification(importedUtils, deduplicationTrackerRef.current, badgeUpdaterRef.current);
          }).catch((error) => {
            console.error('🔔 NotificationModal: Failed to load notification utils:', error);
            // Fallback: add notification without normalization if utils fail to load
            setEvent((prevEvents) => {
              const fallbackNotification = {
                ...newEvent,
                _id: newEvent._id || `fallback_${Date.now()}`,
                timestamp: newEvent.timestamp || new Date().toISOString(),
                noticeType: newEvent.noticeType || 'notification',
                message: newEvent.message || newEvent.title || 'Notification',
                type: newEvent.type || 'notification',
              };
              const isDuplicate = prevEvents.some(evt =>
                evt._id === fallbackNotification._id
              );
              if (isDuplicate) {
                return prevEvents;
              }
              return [fallbackNotification, ...prevEvents];
            });
          });
        }
      }
    });
    return () => {
      unsubscribe();
      // Flush any pending badge updates and cleanup on unmount
      if (badgeUpdaterRef.current) {
        badgeUpdaterRef.current.destroy();
        badgeUpdaterRef.current = null;
      }
    };
  }, [dispatch])

  // Group similar notifications together
  const groupNotifications = useCallback((notifications: any[]) => {
    if (!notifications || !Array.isArray(notifications)) return [];

    // Group notifications by similarity
    const groups = new Map<string, any[]>();
    const ungrouped: any[] = [];

    notifications.forEach((notification) => {
      // Skip grouping for invitations and requests (they should remain individual)
      if (notification.noticeType === 'invitation' || notification.noticeType === 'request') {
        ungrouped.push(notification);
        return;
      }

      // Create a group key based on notification characteristics
      let groupKey: string | null = null;

      // Group by operationType + collection (for data notifications)
      if (notification.operationType && notification.ns?.coll) {
        groupKey = `${notification.operationType}_${notification.ns.coll}`;
      }
      // Group by type (for job notifications)
      else if (notification.type && notification.type.includes('huggingface')) {
        groupKey = `job_${notification.type}`;
      }
      // Group by title pattern (for similar notifications)
      else if (notification.title) {
        // Extract base title (remove dynamic parts like numbers, IDs)
        const baseTitle = notification.title.replace(/\d+/g, '').replace(/\s+/g, '_').toLowerCase();
        groupKey = `title_${baseTitle}`;
      }
      // Group by operationType alone
      else if (notification.operationType) {
        groupKey = `op_${notification.operationType}`;
      }

      if (groupKey) {
        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(notification);
      } else {
        ungrouped.push(notification);
      }
    });

    // Convert groups to display format
    const groupedItems: any[] = [];

    // Add grouped notifications (only if group has 2+ items)
    groups.forEach((groupNotifications, key) => {
      if (groupNotifications.length > 1) {
        // Sort by timestamp (newest first)
        groupNotifications.sort((a, b) => {
          const timeA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : (a.timestamp || 0);
          const timeB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : (b.timestamp || 0);
          return timeB - timeA;
        });

        groupedItems.push({
          _id: `group_${key}`,
          isGroup: true,
          groupKey: key,
          notifications: groupNotifications,
          count: groupNotifications.length,
          // Use the most recent notification as the representative
          representative: groupNotifications[0],
          noticeType: groupNotifications[0].noticeType || 'notification',
        });
      } else {
        // Single notification, add to ungrouped
        ungrouped.push(...groupNotifications);
      }
    });

    // Combine grouped and ungrouped, sort by timestamp
    const allItems = [...groupedItems, ...ungrouped];
    allItems.sort((a, b) => {
      const getTimestamp = (item: any) => {
        if (item.isGroup) {
          return typeof item.representative.timestamp === 'string'
            ? new Date(item.representative.timestamp).getTime()
            : (item.representative.timestamp || 0);
        }
        return typeof item.timestamp === 'string' ? new Date(item.timestamp).getTime() : (item.timestamp || 0);
      };
      return getTimestamp(b) - getTimestamp(a);
    });

    return allItems;
  }, []);

  // Get filtered data for current tab
  const filteredData = useMemo(() => {
    if (!event || !Array.isArray(event)) return [];
    const filtered = event.filter((item: any) => {
      switch (selectedTab) {
        case 0:
          return true;
        case 1:
          return item.noticeType === 'invitation';
        case 2:
          return item.noticeType === 'request';
        default:
          return true;
      }
    });

    // Group similar notifications
    return groupNotifications(filtered);
  }, [event, selectedTab, groupNotifications]);

  // Empty placeholder component based on selected tab
  const EmptyPlaceholder = useMemo(() => {
    const getEmptyContent = () => {
      switch (selectedTab) {
        case 0: // All
          return {
            icon: <AntDesign name="notification" size={64} color={theme ? '#94a3b8' : '#64748b'} />,
            title: t('No notifications yet'),
            message: t('You\'re all caught up! New notifications will appear here.'),
          };
        case 1: // Invitations
          return {
            icon: <MaterialIcons name="mail" size={64} color={theme ? '#94a3b8' : '#64748b'} />,
            title: t('No invitations'),
            message: t('You don\'t have any pending invitations at the moment.'),
          };
        case 2: // Requests
          return {
            icon: <AntDesign name="question-circle" size={64} color={theme ? '#94a3b8' : '#64748b'} />,
            title: t('No requests'),
            message: t('You don\'t have any pending requests right now.'),
          };
        default:
          return {
            icon: <AntDesign name="notification" size={64} color={theme ? '#94a3b8' : '#64748b'} />,
            title: t('No items'),
            message: t('No items to display.'),
          };
      }
    };

    const content = getEmptyContent();

    return () => (
      <Animatable.View
        animation="fadeIn"
        duration={400}
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: 60,
          paddingHorizontal: 32,
          minHeight: 300,
        }}
      >
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            maxWidth: 320,
          }}
        >
          {content.icon}
          <Text
            style={{
              fontSize: 20,
              fontWeight: '600',
              color: getModernTextColor(theme, 'primary'),
              textAlign: 'center',
            }}
          >
            {content.title}
          </Text>
          <Text
            style={{
              fontSize: 14,
              color: getModernTextColor(theme, 'secondary'),
              textAlign: 'center',
              lineHeight: 20,
            }}
          >
            {content.message}
          </Text>
        </View>
      </Animatable.View>
    );
  }, [selectedTab, theme]);


  return (
    <Modal
      animationIn={'slideInRight'}
      animationOut={'slideOutRight'}
      isVisible={visibleState}
      coverScreen={false}
      backdropOpacity={0}
      onBackdropPress={() => dispatch({ type: HIDE_NOTIFICATIONS_PANEL })}
      style={{ flex: 1, justifyContent: 'flex-end', alignContent: "flex-start", flexDirection: 'row', zIndex: 10 }}
    >
      <Animatable.View
        animation="slideInRight"
        duration={400}
        style={[
          getModernCardStyle(theme, 'large'),
          {
            elevation: 20,
            overflow: 'hidden',
            paddingHorizontal: 24,
            minHeight: Dimensions.get('window').height,
            paddingBottom: 80,
            paddingTop: 40,
            minWidth: 600,
            // paddingRight: 50,
            maxWidth: 450,
            width: "40%",
            alignSelf: "stretch",
            flex: 1,
            borderTopLeftRadius: 24,
            borderBottomLeftRadius: 24,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderLeftWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
          }
        ]}
      >
        <CloseButton handleClose={() => dispatch({ type: HIDE_NOTIFICATIONS_PANEL })} top={20} right={12} />
        {/* Header */}
        <Animatable.View
          animation="fadeInDown"
          delay={200}
          style={{
            paddingBottom: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            marginBottom: 20,
          }}
        >
          <Text
            selectable={false}
            style={{
              fontSize: 28,
              fontWeight: '700',
              color: getModernTextColor(theme, 'primary'),
              textAlign: "left",
              letterSpacing: 0.5,
            }}
          >
            {t("Notifications")}
          </Text>
        </Animatable.View>

        {/* Tabs */}
        <Animatable.View
          animation="fadeInUp"
          delay={300}
          style={{ marginBottom: 20 }}
        >
          <Tabs value={selectedTab} onChange={({ eventPhase }) => setSelectedTab(eventPhase)}>
            <Tab
              onClick={() => setSelectedTab(0)}
              style={{
                color: getModernTextColor(theme, 'primary'),
                fontWeight: '600',
                fontSize: 16,
              }}
              label="All"
              value={0}
            />
            <Tab
              onClick={() => setSelectedTab(1)}
              style={{
                color: getModernTextColor(theme, 'primary'),
                fontWeight: '600',
                fontSize: 16,
              }}
              label="Invitations"
              value={1}
            />
            <Tab
              onClick={() => setSelectedTab(2)}
              style={{
                color: getModernTextColor(theme, 'primary'),
                fontWeight: '600',
                fontSize: 16,
              }}
              label="Requests"
              value={2}
            />
          </Tabs>
        </Animatable.View>

        {/* Content */}
        <Animated.View style={{ flexWrap: 'wrap', flex: 4, alignSelf: 'stretch' }}>
          <FlatList
            extraData={filteredData}
            nestedScrollEnabled={true}
            contentContainerStyle={{
              paddingHorizontal: 8,
              gap: Platform.select({ default: 8, web: 12 }),
              paddingVertical: 8,
              paddingBottom: 20,
              flexGrow: filteredData.length === 0 ? 1 : 0,
            }}
            style={{
              flex: 3,
              marginHorizontal: -8,
              paddingHorizontal: 8,
              minHeight: 200,
              minWidth: "100%",
              alignSelf: 'stretch',
            }}
            windowSize={10}
            showsVerticalScrollIndicator={false}
            data={filteredData}
            ListEmptyComponent={EmptyPlaceholder}
            renderItem={({ item, index }) => {
              // Handle grouped notifications
              if (item?.isGroup) {
                return (
                  <ErrorBoundary>
                    <GroupedNotificationItem group={item} theme={theme} />
                  </ErrorBoundary>
                );
              }

              // Handle individual notifications
              switch (item?.noticeType) {
                case 'notification':
                  return (
                    <ErrorBoundary>
                      <NoticeItem item={item} height={undefined} />
                    </ErrorBoundary>
                  )
                case 'request':
                  return (
                    <ErrorBoundary>
                      <RequestItem request={item} onAccept={_onAccept} onReject={_onReject} />
                    </ErrorBoundary>
                  )
                case 'invitation':
                  return (
                    <ErrorBoundary>
                      <InviteItem request={item} onAccept={_onAccept} onReject={_onReject} />
                    </ErrorBoundary>
                  )
                default:
                  return (
                    <ErrorBoundary>
                      <NotificationItem item={item} />
                    </ErrorBoundary>
                  )
              }
            }}
          />
        </Animated.View>
      </Animatable.View>
    </Modal>
  );
};

export default NotificationModal;